/**
 * @file MessagesSent.js
 * @description Card for the Messages Sent dashboard.
 */

import React, { useEffect, useState } from 'react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from 'recharts';
import { toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import {
    DASHBOARD_MESSAGES_SENT_DATE_FORMAT_DATA,
    DASHBOARD_MESSAGES_SENT_DATE_FORMAT_X_AXIS,
    DASHBOARD_MESSAGES_SENT_DATE_FORMAT_TOOLTIP,
    DASHBOARD_MESSAGES_SENT_MAX_X_AXIS_LABELS,
} from './DashboardUtils';
import NotEnoughData from './NotEnoughData';
import './MessagesSent.scss';
import CustomDashboardTooltip from '../helpers/CustomDashboardTooltip';

// Returns the formatted date string using UTC in the given date format
const getFormattedUTCDate = (
    dateValue,
    dateFormat = DASHBOARD_MESSAGES_SENT_DATE_FORMAT_X_AXIS,
) => {
    return format(toZonedTime(new Date(dateValue), 'UTC'), dateFormat);
};

const appendDateValueAndLabelFields = data => {
    const dataAppendedFields = data.map(item => {
        const dateValue = new Date(item.date).getTime();
        const dateLabel = getFormattedUTCDate(
            dateValue,
            DASHBOARD_MESSAGES_SENT_DATE_FORMAT_X_AXIS,
        );
        return {
            ...item,
            dateValue,
            dateLabel,
        };
    });
    return dataAppendedFields;
};

// Returns an array of data that is evenly spaced out based on the given number of maxTicks to display.
// Always includes the startDate and endDate (unless given data that include a date equivalent to the startDate or equivalent to the endDate)
const getXAxisDataEvenlySpaced = (
    data,
    maxTicks = DASHBOARD_MESSAGES_SENT_MAX_X_AXIS_LABELS,
    startDate,
    endDate,
) => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const formattedStart = format(
        new Date(startDate),
        DASHBOARD_MESSAGES_SENT_DATE_FORMAT_DATA,
    );
    const formattedEnd = format(
        new Date(endDate),
        DASHBOARD_MESSAGES_SENT_DATE_FORMAT_DATA,
    );

    // Check if data already has items for startDate or endDate
    const hasStart = data.some(item => item.date === formattedStart);
    const hasEnd = data.some(item => item.date === formattedEnd);

    // If the data doesn't contain the startDate/endDate, then add it to the array of data, otherwise, don't add them
    const combinedData = [
        ...(hasStart
            ? []
            : [
                  {
                      count: 0,
                      date: formattedStart,
                  },
              ]),
        ...data,
        ...(hasEnd
            ? []
            : [
                  {
                      count: 0,
                      date: formattedEnd,
                  },
              ]),
    ];

    // Normalize all with appended dateValue and dateLabel
    const appendedData = appendDateValueAndLabelFields(combinedData);
    const dataLength = appendedData.length;

    // If the length of the given data is less than the number of maxTicks, then we display all data points.
    if (dataLength <= maxTicks) {
        return appendedData.map(item => ({
            ...item,
            isXAxisTick: true,
        }));
    }
    // Otherwise, we evenly space out the dates to display
    else {
        const step = (dataLength - 1) / (maxTicks - 1);
        const selectedIndexes = Array.from({ length: maxTicks }, (_, i) =>
            Math.round(i * step),
        );

        return appendedData.map((item, index) => ({
            ...item,
            isXAxisTick: selectedIndexes.includes(index),
        }));
    }
};

// Returns all the response from the API with some additional fields appended to evenly space out data on the chart
const mapApiResponseToMessagesSentData = (
    dataFromApi,
    maxTicks = DASHBOARD_MESSAGES_SENT_MAX_X_AXIS_LABELS,
    startDate,
    endDate,
) => {
    if (!Array.isArray(dataFromApi) || !dataFromApi) return [];

    // Calculates evenly spaced tick labels to display on the XAxis
    const responseData = getXAxisDataEvenlySpaced(
        dataFromApi,
        maxTicks,
        startDate,
        endDate,
    );

    return responseData;
};

const MessagesSent = ({ messagesSentData, startDate, endDate }) => {
    if (!messagesSentData || messagesSentData?.length === 0) {
        return <NotEnoughData />;
    }

    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        setChartData(
            mapApiResponseToMessagesSentData(
                messagesSentData,
                DASHBOARD_MESSAGES_SENT_MAX_X_AXIS_LABELS,
                startDate,
                endDate,
            ),
        );
    }, [messagesSentData, startDate, endDate]);

    return (
        <div className="messages-sent-container">
            <ResponsiveContainer width="100%">
                <AreaChart data={chartData} margin={0}>
                    <defs>
                        <linearGradient
                            id="messagesGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor="#F9978A"
                                stopOpacity={1}
                            />
                            <stop
                                offset="100%"
                                stopColor="#9391FC"
                                stopOpacity={1}
                            />
                        </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} />
                    <XAxis
                        dataKey="dateLabel"
                        ticks={chartData
                            .filter(d => d.isXAxisTick)
                            .map(d => d.dateLabel)}
                        tick={{
                            fill: 'rgba(43, 48, 52, 0.40)',
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: 12,
                            dy: 10,
                        }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis
                        dataKey="count"
                        allowDecimals={false}
                        tick={{
                            fill: 'rgba(43, 48, 52, 0.40)',
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: 9,
                            dx: -10,
                        }}
                        tickFormatter={value => value.toLocaleString()}
                        interval="preserveStartEnd"
                        axisLine={false}
                        tickLine={false}
                    />
                    <Tooltip
                        content={({ active, payload }) => {
                            if (active && payload?.length > 0) {
                                const { dateValue, count } = payload[0].payload;
                                const formattedTooltipHeaderLabel =
                                    getFormattedUTCDate(
                                        dateValue,
                                        DASHBOARD_MESSAGES_SENT_DATE_FORMAT_TOOLTIP,
                                    );
                                const formattedTooltipCountText =
                                    count === 1
                                        ? 'message sent'
                                        : 'messages sent';
                                const formattedTooltipContent = `${count.toLocaleString()} ${formattedTooltipCountText}`;
                                return (
                                    <CustomDashboardTooltip
                                        active
                                        customContent={
                                            <div className="custom-messages-sent-tooltip">
                                                <div className="messages-sent-tooltip-header-label">
                                                    {
                                                        formattedTooltipHeaderLabel
                                                    }
                                                </div>
                                                <div className="messages-sent-tooltip-content">
                                                    {formattedTooltipContent}
                                                </div>
                                            </div>
                                        }
                                    />
                                );
                            }
                            return null;
                        }}
                    />
                    <Area
                        type="monotone"
                        dataKey="count"
                        stroke="url(#messagesGradient)"
                        fill="url(#messagesGradient)"
                        fillOpacity={1}
                        activeDot={{
                            r: 6,
                            stroke: '#6366f1',
                            strokeWidth: 3,
                            fill: '#fff',
                        }}
                        // dot={{ r: 5, stroke: '#6366f1', strokeWidth: 2, fill: '#fff' }} // OPTIONAL: This renders a 'dot' at each data point
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MessagesSent;
